<template>
  <section>
    <div class="d-flex justify-content-between align-items-center">
      <h4>{{ $t('EnduserChat.tab_usage') }}</h4>
      <div class="d-flex align-items-center gap-2">
        <label class="small text-muted mb-0">{{ $t('EnduserChat.usage_month_label') }}</label>
        <select class="form-select form-select-sm" style="width:auto" v-model="selectedMonth" @change="load">
          <option v-for="m in months" :key="m.value" :value="m.value">{{ m.label }}</option>
        </select>
      </div>
    </div>
    <hr>

    <div v-if="loading" class="text-muted">{{ $t('EnduserChat.loading') }}</div>
    <div v-else>
      <div v-if="usage.auto_disabled_at && !usage.is_enabled" class="alert alert-warning">
        ⚠️ ハード上限超過により AI チャットが自動的に非表示になりました（{{ usage.auto_disabled_at }}）。
      </div>

      <div class="row g-3">
        <!-- 警告閾値ゲージ -->
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <div class="small text-muted">{{ $t('EnduserChat.usage_points') }}</div>
              <div class="fs-3 fw-bold">
                {{ usage.point_sum.toLocaleString() }}
                <span class="fs-6 text-muted">/ {{ warnLabel }}</span>
              </div>
              <div class="progress" style="height:8px">
                <div class="progress-bar" :class="warnBarClass" :style="{width: warnPct + '%'}"></div>
              </div>
              <div class="small text-muted mt-2">{{ $t('EnduserChat.usage_warning_hint') }}</div>
            </div>
          </div>
        </div>

        <!-- ハード上限ゲージ -->
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <div class="small text-muted">{{ $t('EnduserChat.usage_hard_limit') }}</div>
              <div class="fs-3 fw-bold">
                {{ usage.point_sum.toLocaleString() }}
                <span class="fs-6 text-muted">/ {{ hardLabel }}</span>
              </div>
              <div class="progress" style="height:8px">
                <div class="progress-bar bg-primary" :style="{width: hardPct + '%'}"></div>
              </div>
              <div class="small text-muted mt-2">{{ $t('EnduserChat.usage_hard_hint') }}</div>
            </div>
          </div>
        </div>

        <!-- 未解決率 -->
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <div class="small text-muted">{{ $t('EnduserChat.usage_unresolved_rate') }}</div>
              <div class="fs-3 fw-bold">
                {{ unresolvedRate }}<span class="fs-6 text-muted"> %</span>
              </div>
              <div class="progress" style="height:8px">
                <div class="progress-bar bg-danger" :style="{width: unresolvedRate + '%'}"></div>
              </div>
              <div class="small text-muted mt-2">
                {{ usage.unresolved_count }} / {{ usage.talk_count }} 会話
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1">
        <!-- 解決率 -->
        <div class="col-md-4">
          <div class="card h-100">
            <div class="card-body">
              <div class="small text-muted">{{ $t('EnduserChat.usage_resolved_rate') }}</div>
              <div class="fs-3 fw-bold">
                {{ resolvedRate }}<span class="fs-6 text-muted"> %</span>
              </div>
              <div class="progress" style="height:8px">
                <div class="progress-bar bg-success" :style="{width: resolvedRate + '%'}"></div>
              </div>
              <div class="small text-muted mt-2">
                {{ usage.resolved_count }} / {{ usage.talk_count }} 会話
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card"><div class="card-body">
            <div class="small text-muted">{{ $t('EnduserChat.usage_messages') }}</div>
            <div class="fs-4 fw-bold">{{ usage.message_amount.toLocaleString() }}</div>
          </div></div>
        </div>
        <div class="col-md-4">
          <div class="card"><div class="card-body">
            <div class="small text-muted">会話数（当月）</div>
            <div class="fs-4 fw-bold">{{ usage.talk_count.toLocaleString() }}</div>
          </div></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent } from "vue";
import { enduserChatApi } from "@/api";

export default defineComponent({
  name: "EnduserChatUsageView",
  data() {
    return {
      loading: true,
      months: [],
      selectedMonth: "",
      usage: {
        usage_month: "", point_sum: 0, message_amount: 0,
        talk_count: 0, unresolved_count: 0, resolved_count: 0,
        point_warning_threshold: null, point_hard_limit: null,
        is_enabled: true, auto_disabled_at: null,
      },
    };
  },
  computed: {
    warnLabel() {
      return this.usage.point_warning_threshold != null
        ? this.usage.point_warning_threshold.toLocaleString() + " pt" : "未設定";
    },
    hardLabel() {
      return this.usage.point_hard_limit != null
        ? this.usage.point_hard_limit.toLocaleString() + " pt" : "未設定";
    },
    warnPct() {
      const t = this.usage.point_warning_threshold;
      if (!t) return 0;
      return Math.min(100, Math.round((this.usage.point_sum / t) * 100));
    },
    hardPct() {
      const t = this.usage.point_hard_limit;
      if (!t) return 0;
      return Math.min(100, Math.round((this.usage.point_sum / t) * 100));
    },
    warnBarClass() {
      if (this.warnPct >= 100) return "bg-danger";
      if (this.warnPct >= 80) return "bg-warning";
      return "bg-success";
    },
    unresolvedRate() {
      if (!this.usage.talk_count) return 0;
      return Math.round((this.usage.unresolved_count / this.usage.talk_count) * 100);
    },
    resolvedRate() {
      if (!this.usage.talk_count) return 0;
      return Math.round((this.usage.resolved_count / this.usage.talk_count) * 100);
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.usage = await enduserChatApi.getUsage(this.selectedMonth || undefined);
      } catch (e) {
        console.error("failed to load usage", e);
      } finally {
        this.loading = false;
      }
    },
    async loadMonths() {
      try {
        this.months = await enduserChatApi.getUsageMonths();
        // 既定は最新月（一覧は降順）
        if (this.months.length && !this.selectedMonth) {
          this.selectedMonth = this.months[0].value;
        }
      } catch (e) {
        console.error("failed to load usage months", e);
      }
    },
  },
  async mounted() {
    await this.loadMonths();
    await this.load();
  },
});
</script>
